// pages/module-pages/job-offers/JobOffersPage.tsx
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Filter, X, Eye, EyeOff } from "lucide-react";
import PageLayout from "../../../layouts/PageLayout";
import DeleteConfirmationModal from "../../../components/ui/DeleteConfirmationModal";
import MenuModal, { Menu } from "../../../components/ui/MenuModal";
import useJobOffers from "../../../hooks/features/job-offers/useJobOffers";
import { JobOffer } from "../../../types/features/JobOffer";
import { alertSuccess, alertError } from "../../../helpers/alertError";
import JobOffersList from "../../../components/shared/job-offers/JobOfferLists";
import { useProduct } from "../../../hooks/core/useProduct";

export default function JobOffersPage() {
  const navigate = useNavigate();
  const { selectedProduct } = useProduct();
  const { jobOffers, deleteJobOffer, deleteManyJobOffers, toggleVisibility, loading } = useJobOffers();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContractType, setSelectedContractType] = useState<string>("");
  const [selectedStatus, setSelectedStatus] = useState<string>("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedJobOffer, setSelectedJobOffer] = useState<JobOffer | null>(null);
  const [jobOfferToDelete, setJobOfferToDelete] = useState<JobOffer | null>(null);
  const [itemsToDelete, setItemsToDelete] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ✅ Filtrer les offres par produit
  const jobOffersByProduct = useMemo(() => {
    if (selectedProduct && selectedProduct !== 'all') {
      return jobOffers.filter((o) => o.product_name === selectedProduct);
    }
    return jobOffers;
  }, [jobOffers, selectedProduct]);

  // ✅ Stats basées sur les offres filtrées par produit
  const stats = {
    total: jobOffersByProduct.length,
    cdi: jobOffersByProduct.filter((o) => o.contract_type === "cdi").length,
    cdd: jobOffersByProduct.filter((o) => o.contract_type === "cdd").length,
    freelance: jobOffersByProduct.filter((o) => o.contract_type === "freelance").length,
    internship: jobOffersByProduct.filter((o) => o.contract_type === "internship").length,
    visible: jobOffersByProduct.filter((o) => o.is_visible).length,
  };

  // ✅ Filtrer les offres (recherche + contrat + statut) à partir des offres déjà filtrées par produit
  const filteredJobOffers = useMemo(() => {
    let result = jobOffersByProduct;

    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (o) =>
          o.title.toLowerCase().includes(searchLower) ||
          o.location.toLowerCase().includes(searchLower) ||
          o.title_en.toLowerCase().includes(searchLower)
      );
    }

    if (selectedContractType) {
      result = result.filter((o) => o.contract_type === selectedContractType);
    }

    if (selectedStatus) {
      const isVisible = selectedStatus === "visible";
      result = result.filter((o) => o.is_visible === isVisible);
    }

    return result;
  }, [jobOffersByProduct, searchTerm, selectedContractType, selectedStatus]);

  const handleToggleVisibility = async (jobOffer: JobOffer) => {
    const success = await toggleVisibility(jobOffer.id, jobOffer.is_visible);
    if (success) {
      alertSuccess(jobOffer.is_visible ? "Offre masquée" : "Offre affichée");
    } else {
      alertError("Erreur lors du changement de statut");
    }
  };

  const handleDelete = async () => {
    if (!jobOfferToDelete) return;
    setIsDeleting(true);
    const success = await deleteJobOffer(jobOfferToDelete.id);
    setIsDeleting(false);
    if (success) {
      setJobOfferToDelete(null);
      alertSuccess("Offre supprimée avec succès");
    } else {
      alertError("Erreur lors de la suppression");
    }
  };

  const handleDeleteMany = async () => {
    if (!itemsToDelete || itemsToDelete.length === 0) return;
    const success = await deleteManyJobOffers(itemsToDelete);
    if (success) {
      setItemsToDelete(null);
      alertSuccess(`${itemsToDelete.length} offre(s) supprimée(s)`);
    } else {
      alertError("Erreur lors de la suppression multiple");
    }
  };

  const clearFilters = () => {
    setSelectedContractType("");
    setSelectedStatus("");
    setSearchTerm("");
  };

  const hasActiveFilters = selectedContractType || selectedStatus || searchTerm;

  const getActionsMenu = (jobOffer: JobOffer): Menu[] => {
    return [
      {
        label: jobOffer.is_visible ? "Masquer" : "Afficher",
        icon: jobOffer.is_visible ? EyeOff : Eye,
        onClick: () => handleToggleVisibility(jobOffer),
      },
      {
        label: "Modifier",
        icon: Plus,
        onClick: () => navigate(`/admin/job-offers/update?id=${jobOffer.id}`),
      },
      {
        label: "Supprimer",
        icon: Plus,
        onClick: () => setJobOfferToDelete(jobOffer),
      },
    ];
  };

  return (
    <PageLayout title="Gestion des offres d'emploi" className="min-h-screen bg-gray-50">
      <div className="px-6 py-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Offres d'emploi</h1>
              <p className="text-sm text-gray-500 mt-1">Gérez les recrutements de l'entreprise</p>
            </div>
            <button
              onClick={() => navigate("/admin/job-offers/new")}
              className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-xl hover:bg-primary/90"
            >
              <Plus size={18} />
              Nouvelle offre
            </button>
          </div>

          {/* Indicateur de filtre produit */}
          {selectedProduct && selectedProduct !== 'all' && (
            <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200 text-sm text-blue-700">
              Filtré par produit : <span className="font-semibold">{selectedProduct}</span>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4">
              <p className="text-xs text-blue-700/70">Total</p>
              <p className="text-2xl font-bold text-blue-700">{stats.total}</p>
            </div>
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-xl p-4">
              <p className="text-xs text-indigo-700/70">CDI</p>
              <p className="text-2xl font-bold text-indigo-700">{stats.cdi}</p>
            </div>
            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4">
              <p className="text-xs text-yellow-700/70">CDD</p>
              <p className="text-2xl font-bold text-yellow-700">{stats.cdd}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4">
              <p className="text-xs text-purple-700/70">Freelance</p>
              <p className="text-2xl font-bold text-purple-700">{stats.freelance}</p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4">
              <p className="text-xs text-green-700/70">Stage</p>
              <p className="text-2xl font-bold text-green-700">{stats.internship}</p>
            </div>
            <div className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-xl p-4">
              <p className="text-xs text-emerald-700/70">Visibles</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.visible}</p>
            </div>
          </div>

          {/* Recherche */}
          <div className="relative mt-6">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher une offre..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/50"
            />
          </div>

          {/* Filtres */}
          <div className="mt-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-xl transition-all ${showFilters ? "bg-primary text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}
            >
              <Filter size={18} /> Filtrer
              {hasActiveFilters && <span className="w-2 h-2 bg-red-500 rounded-full" />}
            </button>

            {showFilters && (
              <div className="bg-gray-50 rounded-xl p-4 mt-3 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-gray-700">Filtres</h3>
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-sm text-red-600 hover:text-red-700 flex items-center gap-1"
                    >
                      <X size={14} /> Effacer tout
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type de contrat</label>
                    <select
                      value={selectedContractType}
                      onChange={(e) => setSelectedContractType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Tous</option>
                      <option value="cdi">CDI</option>
                      <option value="cdd">CDD</option>
                      <option value="freelance">Freelance</option>
                      <option value="internship">Stage</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">Tous</option>
                      <option value="visible">Visible</option>
                      <option value="hidden">Masqué</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-8 max-w-7xl mx-auto">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <JobOffersList
            jobOffers={filteredJobOffers}
            onAction={(jobOffer) => setSelectedJobOffer(jobOffer)}
            onSelectJobOffers={(selected) => setItemsToDelete(selected.map((o) => o.id))}
            selectable={true}
            selectActions={[
              {
                label: "Supprimer",
                onClick: (selected) => setItemsToDelete(selected.map((o) => o.id)),
                className: "bg-red-600 text-white",
              },
            ]}
          />
        )}
      </div>

      {/* Menu modal */}
      {selectedJobOffer && (
        <MenuModal
          title={selectedJobOffer.title}
          isOpen={!!selectedJobOffer}
          onClose={() => setSelectedJobOffer(null)}
          icon={null}
          menu={getActionsMenu(selectedJobOffer)}
        />
      )}

      {/* Modals suppression */}
      <DeleteConfirmationModal
        isOpen={!!jobOfferToDelete}
        onClose={() => setJobOfferToDelete(null)}
        onConfirm={handleDelete}
        title="Supprimer l'offre"
        message={`Supprimer "${jobOfferToDelete?.title}" ?`}
        confirmText={isDeleting ? "Suppression..." : "Supprimer"}
      />

      <DeleteConfirmationModal
        isOpen={!!itemsToDelete && itemsToDelete.length > 0}
        onClose={() => setItemsToDelete(null)}
        onConfirm={handleDeleteMany}
        title="Suppression multiple"
        message={`Supprimer ${itemsToDelete?.length} offre(s) ?`}
      />
    </PageLayout>
  );
}